import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
const isSpeechSupported = !!SpeechRecognitionAPI;

/**
 * SpeechToTextButton — inline mic widget for text fields.
 *
 * Idle state:  small mic icon
 * Active state: "Listening..." pill with animated bars + stop button
 *
 * Props:
 *   currentValue   — current field value (snapshot when mic starts)
 *   onLiveUpdate   — called on every speech event with the full text to set on the field
 *   onFinalCommit  — called once when mic stops with only the newly added text
 *   disabled       — disable the button
 */
const SpeechToTextButton = ({
  currentValue = '',
  onLiveUpdate,
  onFinalCommit,
  disabled = false,
}) => {
  const [isListening, setIsListening] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const baseTextRef = useRef('');
  const committedRef = useRef('');
  const interimRef = useRef('');
  // Track the last text we sent to onLiveUpdate so we can preserve it on stop
  const lastSentTextRef = useRef('');
  // Track whether we intentionally stopped (vs auto-end)
  const intentionalStopRef = useRef(false);

  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
      recognitionRef.current = null;
    }
  }, []);

  const buildFullText = useCallback(() => {
    const base = baseTextRef.current;
    const committed = committedRef.current.trim();
    const interim = interimRef.current.trim();
    const newText = [committed, interim].filter(Boolean).join(' ');
    if (!base && !newText) return '';
    if (!base) return newText;
    if (!newText) return base;
    return `${base} ${newText}`;
  }, []);

  const stopListening = useCallback(() => {
    // Set the intentional stop flag FIRST — this prevents any late-arriving
    // async onresult/onend events from overwriting the text after we commit it.
    // This flag stays true until the next startListening() call.
    intentionalStopRef.current = true;
    isListeningRef.current = false;
    setIsListening(false);

    // Build the final text BEFORE aborting, while refs still have data
    const finalText = buildFullText();
    const allNewText = [committedRef.current.trim(), interimRef.current.trim()]
      .filter(Boolean)
      .join(' ');

    if (recognitionRef.current) {
      // Use abort() instead of stop(). stop() tries to process remaining audio
      // and fires one last async onresult event AFTER this function returns,
      // which would overwrite our committed text with near-empty data.
      // abort() discards pending audio and does not fire onresult.
      try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
      recognitionRef.current = null;
    }

    // Commit the final text including any pending interim words
    if (finalText) {
      onLiveUpdate?.(finalText);
    }
    if (allNewText) {
      onFinalCommit?.(allNewText);
    }

    // Clear refs after everything is committed
    committedRef.current = '';
    interimRef.current = '';
    baseTextRef.current = '';
    lastSentTextRef.current = '';
    // NOTE: intentionalStopRef stays true — it is only reset in startListening()
  }, [onFinalCommit, onLiveUpdate, buildFullText]);

  const requestMicPermission = useCallback(async () => {
    // Try to explicitly request permission via navigator.permissions or getUserMedia
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        if (result.state === 'denied') {
          setPermissionDenied(true);
          return false;
        }
        if (result.state === 'granted') {
          setPermissionDenied(false);
          return true;
        }
      }
    } catch (_) {
      // permissions API not supported for microphone in this browser, fall through
    }

    // Prompt the user by requesting a stream (this triggers the browser permission dialog)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Permission granted — stop the stream immediately, we just needed the permission
      stream.getTracks().forEach((track) => track.stop());
      setPermissionDenied(false);
      return true;
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      }
      return false;
    }
  }, []);

  const startListening = useCallback(async () => {
    if (!isSpeechSupported || disabled) return;

    if (isListeningRef.current) {
      stopListening();
      return;
    }

    // Always check/request microphone permission before starting
    const hasPermission = await requestMicPermission();
    if (!hasPermission) return;

    baseTextRef.current = currentValue || '';
    committedRef.current = '';
    interimRef.current = '';
    lastSentTextRef.current = '';
    intentionalStopRef.current = false;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-GB';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      // If we've already stopped intentionally, ignore any late-arriving events.
      // recognition.stop()/abort() can fire async events after stopListening() returns.
      if (intentionalStopRef.current) return;

      // Rebuild committed and interim from ALL results each time.
      // This prevents the issue where processing from event.resultIndex
      // could cause text duplication or replacement when the engine
      // re-evaluates earlier results.
      let allFinal = '';
      let latestInterim = '';

      for (let i = 0; i < event.results.length; i += 1) {
        const { transcript } = event.results[i][0];
        if (event.results[i].isFinal) {
          allFinal += ` ${transcript}`;
        } else {
          latestInterim += transcript;
        }
      }

      committedRef.current = allFinal;
      interimRef.current = latestInterim;

      const fullText = buildFullText();
      lastSentTextRef.current = fullText;
      onLiveUpdate?.(fullText);
    };

    recognition.onerror = (event) => {
      if (intentionalStopRef.current) return;
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      if (event.error === 'not-allowed') {
        setPermissionDenied(true);
        stopListening();
        return;
      }
      stopListening();
    };

    recognition.onend = () => {
      // If we intentionally stopped, do nothing — stopListening already handled cleanup
      if (intentionalStopRef.current) return;

      if (isListeningRef.current) {
        // Auto-restart for continuous listening (e.g. silence timeout)
        try { recognition.start(); } catch (_) { stopListening(); }
      } else {
        // Unexpected end — make sure we preserve whatever text was last sent
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (_) {
      stopListening();
    }
  }, [disabled, currentValue, onLiveUpdate, stopListening, buildFullText, requestMicPermission]);

  // Not supported: show disabled mic with tooltip (important for mobile)
  if (!isSpeechSupported) {
    return (
      <Tooltip title="Voice input is not supported on this browser. Try using Chrome on a desktop." arrow>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            cursor: 'default',
            opacity: 0.35,
            color: '#919EAB',
          }}
        >
          <Iconify icon="mdi:microphone-off" width={18} height={18} />
        </Box>
      </Tooltip>
    );
  }

  // Permission denied: show mic with warning tooltip
  if (permissionDenied && !isListening) {
    return (
      <Tooltip title="Microphone access was denied. Please allow microphone access in your browser settings and try again." arrow>
        <Box
          onClick={startListening}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            cursor: 'pointer',
            color: '#FF5630',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'rgba(255,86,48,0.08)',
            },
          }}
        >
          <Iconify icon="mdi:microphone-off" width={18} height={18} />
        </Box>
      </Tooltip>
    );
  }

  // Active: "Listening..." pill
  if (isListening) {
    return (
      <Box
        onClick={stopListening}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: '10px',
          py: '4px',
          borderRadius: '20px',
          bgcolor: '#FFF5F2',
          border: '1px solid #FFAC82',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: '#FFE9E0', borderColor: '#FF5630' },
        }}
      >
        {/* Animated sound bars */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 14 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                width: 2.5,
                borderRadius: 1,
                bgcolor: '#FF5630',
                animation: `soundBar 0.8s ease-in-out ${i * 0.15}s infinite alternate`,
                '@keyframes soundBar': {
                  '0%': { height: '4px' },
                  '100%': { height: '14px' },
                },
              }}
            />
          ))}
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#B71D18', lineHeight: 1, whiteSpace: 'nowrap' }}>
          Listening...
        </Typography>
        <Iconify icon="mdi:stop-circle" width={14} height={14} sx={{ color: '#FF5630', flexShrink: 0 }} />
      </Box>
    );
  }

  // Idle: simple mic icon
  return (
    <Tooltip title="Click to start voice input" arrow>
      <Box
        onClick={startListening}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          color: '#919EAB',
          transition: 'all 0.15s ease',
          '&:hover': disabled ? {} : {
            bgcolor: 'rgba(0,167,111,0.08)',
            color: '#00A76F',
          },
        }}
      >
        <Iconify icon="mdi:microphone" width={18} height={18} />
      </Box>
    </Tooltip>
  );
};

SpeechToTextButton.propTypes = {
  currentValue: PropTypes.string,
  onLiveUpdate: PropTypes.func.isRequired,
  onFinalCommit: PropTypes.func,
  disabled: PropTypes.bool,
};

export default SpeechToTextButton;
